import type { IPack } from './types.js';

/** Folder categories (the third level of Client › Year › Category). */
export const ACCOUNTING_CATEGORIES = {
  statements: 'Financial & Bank Statements',
  expenses: 'Invoices & Expenses',
  tax: 'Tax & Compliance',
  payroll: 'Payroll & HR',
  engagement: 'Engagement & Contracts',
  general: 'General'
} as const;

const C = ACCOUNTING_CATEGORIES;

/**
 * Accounting and bookkeeping firms (the original preset). Canadian-first: T4, T5, HST, CRA notices.
 * Every document belongs to a client of the firm.
 */
export const ACCOUNTING_CA: IPack = {
  id: 'accounting-ca',
  name: 'Accounting or bookkeeping firm',
  jurisdiction: 'CA',
  entity: { one: 'client', many: 'clients' },
  ai: {
    intro: 'These files belong to an accounting or bookkeeping firm. For each file, read the facts it will be named and filed by.',
    clientMeaning: 'client: the firm\'s client this document belongs to — the account holder of a statement, the taxpayer or employer on a tax ' +
      'or payroll form, the business that paid a bill or receipt, the business that issued an invoice. Never the bank, the vendor, the ' +
      'CRA, or the accounting firm itself.',
    docTypeHint: ' An invoice the client issued to its customer is Invoice; an invoice the client received from a vendor or supplier is Bill.',
    dateHint: ' For a year-only form (T4, T5), use December 31 of that year.'
  },
  defaultFolders: ['client', 'year', 'category'],
  categories: Object.values(C),
  docTypes: [
  { id: 'BankStatement', label: 'Bank statement', category: C.statements, dateMeaning: 'statement period end date', detailMeaning: 'bank short name (TD, RBC, BMO, CIBC, Scotiabank, Desjardins, ATB…)' },
  { id: 'CreditCardStatement', label: 'Credit card statement', category: C.statements, dateMeaning: 'statement period end date', detailMeaning: 'card issuer short name, e.g. AmexBusiness, RBCVisa' },
  { id: 'Invoice', label: 'Invoice (issued by the client)', category: C.expenses, dateMeaning: 'invoice date', detailMeaning: 'invoice number' },
  { id: 'Bill', label: 'Bill (received by the client)', category: C.expenses, dateMeaning: 'bill date', detailMeaning: 'vendor name', detailRule: 'issuer' },
  { id: 'Receipt', label: 'Receipt', category: C.expenses, dateMeaning: 'purchase date', detailMeaning: 'vendor name' },
  { id: 'PurchaseOrder', label: 'Purchase order', category: C.expenses, dateMeaning: 'order date', detailMeaning: 'purchase order number' },
  { id: 'T4', label: 'T4 slip', category: C.payroll, dateMeaning: 'December 31 of the tax year', detailMeaning: 'employee last name' },
  { id: 'T4Summary', label: 'T4 summary', category: C.payroll, dateMeaning: 'December 31 of the tax year', detailMeaning: 'leave empty' },
  { id: 'PayrollRegister', label: 'Payroll register', category: C.payroll, dateMeaning: 'pay period end date', detailMeaning: 'pay period, e.g. PP14' },
  { id: 'T5', label: 'T5 slip', category: C.tax, dateMeaning: 'December 31 of the tax year', detailMeaning: 'payer, e.g. the bank' },
  { id: 'HSTReturn', label: 'GST/HST return', category: C.tax, dateMeaning: 'reporting period end date', detailMeaning: 'period, e.g. Q2 or Annual', detailRule: 'taxPeriod' },
  { id: 'NoticeOfAssessment', label: 'Notice of assessment', category: C.tax, dateMeaning: 'date of the notice', detailMeaning: 'tax year and type, e.g. T2-2024 or HST-Q2' },
  { id: 'TrialBalance', label: 'Trial balance', category: C.statements, dateMeaning: 'as-of date', detailMeaning: 'leave empty' },
  { id: 'GeneralLedger', label: 'General ledger', category: C.statements, dateMeaning: 'period end date', detailMeaning: 'leave empty' },
  { id: 'FinancialStatements', label: 'Financial statements', category: C.statements, dateMeaning: 'fiscal year end date', detailMeaning: 'e.g. Draft or Final' },
  { id: 'EngagementLetter', label: 'Engagement letter', category: C.engagement, dateMeaning: 'date signed or issued', detailMeaning: 'service, e.g. Bookkeeping or YearEnd' },
  { id: 'Contract', label: 'Contract', category: C.engagement, dateMeaning: 'effective date', detailMeaning: 'other party', detailFrom: 'otherParty' },
  { id: 'Correspondence', label: 'Letter or correspondence', category: C.general, dateMeaning: 'date of the letter', detailMeaning: 'sender, e.g. CRA', detailRule: 'issuer' },
  { id: 'Other', label: 'Other', category: C.general, dateMeaning: 'the date the document is about', detailMeaning: 'a short topic' }
  ],
  recordCategories: []
};
